/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { FormField } from '@adornis/formfield/form-field.js';
import { html, type PropertyValueMap } from 'lit';
import { customElement } from 'lit/decorators.js';

@customElement('bh-input-auth-code')
export class BHInputAuthCode extends FormField<string> {
  protected override firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.firstUpdated(_changedProperties);

    const inputs = this.renderRoot.querySelectorAll('#otp-flex > *[id]');
    const host = this;
    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i] as any as Element;
      const dispatchEvent = () => {
        const value = inputs[0].value + inputs[1].value + inputs[2].value + inputs[3].value;
        this.dispatchEvent(new CustomEvent('value-changed', { detail: { value } }));
      };

      input.addEventListener('enter', function (event) {
        event.preventDefault();
      });

      input.addEventListener('paste', function (event) {
        event.preventDefault();

        if (i !== 0) {
          input.value = '';
          return;
        }

        const paste = event.clipboardData.getData('Text');
        const regex = /^[0-9]{4}$/;
        if (!regex.test(paste)) return;

        inputs[0].value = paste[0];
        inputs[1].value = paste[1];
        inputs[2].value = paste[2];
        inputs[3].value = paste[3];
        inputs[3].focus();
        dispatchEvent();
        return;
      });

      input.addEventListener('keydown', async function (event) {
        if (event.key === 'Enter' && i === 3) {
          return host.dispatchEvent(new CustomEvent('enter', { detail: { value: this.value.value } }));
        }

        if (event.key === 'Backspace') {
          event.preventDefault();
          if (input.value) {
            input.value = '';
            return;
          }
          if (i !== 0) inputs[i - 1].focus();
          return;
        }

        const isValidKey = (event.keyCode > 47 && event.keyCode < 58) || (event.keyCode > 95 && event.keyCode < 106);

        if (!isValidKey) {
          // not ctrg + v -> has to be handled in "paste" eventlistener
          if (event.keyCode !== 86) event.preventDefault();
          input.value = '';
          return;
        }

        if (isValidKey) {
          event.preventDefault();
          input.value = event.key;
          if (i !== inputs.length - 1) inputs[i + 1].focus();
        }

        if (event.keyCode > 64 && event.keyCode < 91) {
          if (i !== inputs.length - 1) inputs[i + 1].focus();
        }

        if (i === inputs.length - 1 && input.value !== '') dispatchEvent();
      });
    }
  }

  override render() {
    return html`
      <div class="mb-6 text-center">
        <x-flex id="otp-flex" horizontal center>
          <!-- 1 -->
          ${this._renderInputField('first')}
          <!-- 2 -->
          ${this._renderInputField('second')}
          <!-- 3 -->
          ${this._renderInputField('third')}
          <!-- 4 -->
          ${this._renderInputField('fourth')}
        </x-flex>
      </div>
    `;
  }

  private _renderInputField(id: string) {
    return html` <input class="m-2 form-control" type="number" id=${id} maxlength="4" step="1" /> `;
  }

  /**
   * workaround helper method to actually read inputs when validating otp, because there were users that got the error "Login Code nicht eingegeben" which was not reproducible by any devs
   */
  getCurrentValues() {
    const inputs = this.renderRoot.querySelectorAll<HTMLInputElement>('#otp-flex > *[id]');
    if (Array.from(inputs).length !== 4) throw new Error('More or less than 4 otp inputs: ' + inputs.length);

    return inputs[0]?.value + inputs[1]?.value + inputs[2]?.value + inputs[3]?.value;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        'input[type=number]::-webkit-inner-spin-button, input::-webkit-outer-spin-button': {
          ['-webkit-appearance']: 'none',
          margin: '0',
        },
        '.form-control': {
          // border: 'none',
          background: 'transparent',
          // borderBottom: '3px solid black',
          '-webkit-transition': 'none',
          transition: 'none',
          width: '32px',
          // height: '32px',
          textAlign: 'center',
          ['-webkit-appearance']: 'none',
          margin: '0',
          '-moz-appearance': 'textfield',
          '&:focus': {
            outline: 'none !important',
            borderBottomColor: this.colors.accent.shade(200),
          },
          border: `2px solid ${this.colors.neutral.shade(100)}`,
          color: this.colors.neutral,
          height: '40px',
          fontSize: '20px',
          '&:first-child': {
            borderTopLeftRadius: this.sizes.borderRadius,
            borderBottomLeftRadius: this.sizes.borderRadius,
          },
          '&:not(:first-child)': { borderLeft: 'none' },
          '&:last-child': {
            borderTopRightRadius: this.sizes.borderRadius,
            borderBottomRightRadius: this.sizes.borderRadius,
          },
        },
      },
    ];
  }
}
